import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { oneKlicks } from "../../../SDK/oneclick";
import { OneKlicksFileSDK } from "../../../SDK/OneKlicksFileSDK";
import { useAuth } from "../../../context/AuthContext";

const AgentStatuses = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
    { label: "Suspended", value: "suspended" },
];

const EditAgent = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();

    const agentId = useMemo(() => Number(id), [id]);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
    const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
    const previewUrlRef = useRef(null);
    const [formData, setFormData] = useState({
        agent_uid: "",
        company_id: "",
        name: "",
        email: "",
        phone: "",
        alternate_phone: "",
        status: "active",
        designation: "",
        license_number: "",
        registration_number: "",
        id_number: "",
        experience_years: "",
        address: "",
        city: "",
        state: "",
        country: "",
        postal_code: "",
        password: "",
        confirm_password: "",
        auth_key: "",
        notes: "",
        profile_photo_url: "",
        profile_image: "",
        image: "",
    });

    const hasCompanies = useMemo(() => companies.length > 0, [companies]);

    useEffect(() => {
        if (!agentId) {
            toast.error("Invalid agent reference");
            navigate("/insurance/agents", { replace: true });
            return;
        }
        loadAgent();
        loadCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    const loadAgent = async () => {
        setLoading(true);
        try {
            const response = await oneKlicks.read("", "insurance_agents", { id: agentId }, "", "", "", "0,1");
            
            if (response?.success && Array.isArray(response?.data) && response.data.length > 0) {
                const agent = response.data[0];
                
                // Parse meta if it's a JSON string
                let metaData = {};
                if (agent.meta) {
                    try {
                        metaData = typeof agent.meta === 'string' ? JSON.parse(agent.meta) : agent.meta;
                    } catch (e) {
                        console.warn("Failed to parse meta:", e);
                    }
                }

                const profileImage = agent.profile_image || agent.profile_photo_url || agent.image || "";
                setFormData({
                    agent_uid: agent.agent_uid || agent.uid || agent.agent_code || "",
                    company_id: agent.company_id ? String(agent.company_id) : "",
                    name: agent.full_name || agent.name || "",
                    email: agent.agent_email || agent.email || "",
                    phone: agent.agent_phone || agent.phone || "",
                    alternate_phone: metaData.alternate_phone || agent.alternate_phone || "",
                    status: agent.status || "active",
                    designation: agent.designation || metaData.designation || "",
                    license_number: agent.license_number || metaData.license_number || "",
                    registration_number: agent.registration_number || metaData.registration_number || "",
                    id_number: agent.id_number || metaData.id_number || "",
                    experience_years: agent.experience_years || metaData.experience_years || "",
                    address: agent.address || metaData.address || "",
                    city: agent.city || metaData.city || "",
                    state: agent.state || metaData.state || "",
                    country: agent.country || metaData.country || "",
                    postal_code: agent.postal_code || metaData.postal_code || "",
                    password: "",
                    confirm_password: "",
                    auth_key: agent.auth_key || metaData.auth_key || "",
                    notes: agent.notes || metaData.notes || "",
                    profile_photo_url: profileImage,
                    profile_image: profileImage,
                    image: profileImage,
                });
            } else {
                toast.error("Agent not found");
                navigate("/insurance/agents", { replace: true });
            }
        } catch (error) {
            console.error("Error loading agent:", error);
            toast.error("Failed to load agent details");
            navigate("/insurance/agents", { replace: true });
        } finally {
            setLoading(false);
        }
    };

    const loadCompanies = async () => {
        try {
            const response = await oneKlicks.read("", "insurance_companies", {}, "created_at DESC", "", "", "0,100");
            if (response?.success && Array.isArray(response?.data)) {
                const uniqueCompanies = response.data.filter((item) => item && item.id);
                setCompanies(uniqueCompanies);
            } else {
                setCompanies([]);
            }
        } catch (error) {
            console.error("Failed to load insurance companies:", error);
            setCompanies([]);
        }
    };

    const releasePreviewUrl = () => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            releasePreviewUrl();
        };
    }, []);

    const handleProfilePhotoSelect = async (file) => {
        if (!file) {
            return;
        }

        const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
        if (!validTypes.includes(file.type)) {
            toast.error("Upload a valid image file (JPG, PNG, GIF, or WEBP)");
            return;
        }

        const maxSize = 5 * 1024 * 1024; // 5 MB
        if (file.size > maxSize) {
            toast.error("Image size should be 5 MB or less");
            return;
        }

        releasePreviewUrl();
        const previewUrl = URL.createObjectURL(file);
        previewUrlRef.current = previewUrl;
        setProfilePhotoPreview(previewUrl);
        setProfilePhotoUploading(true);

        try {
            const uploadResult = await OneKlicksFileSDK.uploadFile("insurance/agents", "public", file);
            if (!uploadResult?.file?.root || !uploadResult?.file?.fileName) {
                throw new Error(uploadResult?.error || "Upload failed");
            }

            const remoteUrl = `${uploadResult.file.root}/${uploadResult.file.fileName}`;
            setFormData((prev) => ({ 
                ...prev, 
                profile_photo_url: remoteUrl,
                profile_image: remoteUrl,
                image: remoteUrl
            }));
            toast.success("Profile photo updated");

            releasePreviewUrl();
            setProfilePhotoPreview("");
        } catch (error) {
            console.error("Error uploading profile photo:", error);
            toast.error("Failed to upload profile photo");
            releasePreviewUrl();
            setProfilePhotoPreview("");
        } finally {
            setProfilePhotoUploading(false);
        }
    };

    const handleProfilePhotoRemove = () => {
        releasePreviewUrl();
        setProfilePhotoPreview("");
        setFormData((prev) => ({ 
            ...prev, 
            profile_photo_url: "",
            profile_image: "",
            image: ""
        }));
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.company_id) {
            toast.error("Please select a company");
            return;
        }

        if (!formData.name?.trim()) {
            toast.error("Agent name is required");
            return;
        }

        if (!formData.email?.trim()) {
            toast.error("Agent email is required");
            return;
        }

        if (formData.password && formData.password !== formData.confirm_password) {
            toast.error("Passwords do not match");
            return;
        }

        setSubmitting(true);

        try {
            const timestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
            const metaPayload = {
                alternate_phone: formData.alternate_phone?.trim() || null,
                designation: formData.designation?.trim() || null,
                license_number: formData.license_number?.trim() || null,
                registration_number: formData.registration_number?.trim() || null,
                id_number: formData.id_number?.trim() || null,
                experience_years: formData.experience_years?.trim() || null,
                status: formData.status,
                notes: formData.notes?.trim() || null,
                address: formData.address?.trim() || null,
                city: formData.city?.trim() || null,
                state: formData.state?.trim() || null,
                country: formData.country?.trim() || null,
                postal_code: formData.postal_code?.trim() || null,
            };

            const basePayload = {
                agent_uid: formData.agent_uid?.trim(),
                company_id: Number(formData.company_id),
                name: formData.name?.trim(),
                full_name: formData.name?.trim(),
                email: formData.email?.trim(),
                agent_email: formData.email?.trim(),
                phone: formData.phone?.trim() || null,
                agent_phone: formData.phone?.trim() || null,
                agent_code: formData.agent_uid?.trim(),
                designation: formData.designation?.trim() || null,
                license_number: formData.license_number?.trim() || null,
                registration_number: formData.registration_number?.trim() || null,
                id_number: formData.id_number?.trim() || null,
                address: formData.address?.trim() || null,
                city: formData.city?.trim() || null,
                state: formData.state?.trim() || null,
                country: formData.country?.trim() || null,
                postal_code: formData.postal_code?.trim() || null,
                status: formData.status,
                updated_at: timestamp,
            };

            // Add profile image if provided
            if (formData.profile_photo_url?.trim()) {
                basePayload.profile_photo_url = formData.profile_photo_url.trim();
                basePayload.profile_image = formData.profile_photo_url.trim();
                basePayload.image = formData.profile_photo_url.trim();
            }

            // Only update password if provided
            if (formData.password?.trim()) {
                basePayload.password = formData.password.trim();
                basePayload.password_hash = formData.password.trim();
            }

            // Only update auth_key if provided
            if (formData.auth_key?.trim()) {
                basePayload.auth_key = formData.auth_key.trim();
            }

            const enrichedPayload = {
                ...basePayload,
                meta: JSON.stringify(metaPayload),
            };

            console.log("Updating insurance agent payload:", enrichedPayload);
            const response = await oneKlicks.update("", "insurance_agents", enrichedPayload, { id: agentId });
            console.log("Update agent response:", response);

            if (response?.success) {
                toast.success("Agent updated successfully");
                navigate("/insurance/agents");
            } else {
                toast.error(response?.message || response?.error || "Failed to update agent");
            }
        } catch (error) {
            console.error("Error updating agent:", error);
            toast.error("Failed to update agent");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="subbody" style={{ width: "100%", padding: "2% 4%" }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    <div className="card mb-4" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: "24px 30px", borderRadius: "5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div>
                                    <h5 className="mb-0 font-weight-bold">Edit Insurance Agent</h5>
                                    <p style={{ color: "#6c757d", marginTop: "4px", marginBottom: 0 }}>
                                        Update agent information and details.
                                    </p>
                                </div>
                                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => navigate("/insurance/agents")}
                                        disabled={submitting}
                                        style={{ borderRadius: "5px" }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        form="insurance-agent-form"
                                        className="btn btn-primary"
                                        disabled={submitting || !hasCompanies}
                                        style={{ borderRadius: "5px" }}
                                    >
                                        {submitting ? "Saving..." : "Update Agent"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: "24px 30px", borderRadius: "5px" }}>
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        ) : !hasCompanies ? (
                            <div className="text-center p-5">
                                <p style={{ color: "#6c757d", marginBottom: "16px" }}>
                                    No companies found. Please create an insurance company first.
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => navigate("/insurance/companies/new")}
                                    style={{ borderRadius: "5px" }}
                                >
                                    Add Company
                                </button>
                            </div>
                        ) : (
                            <form id="insurance-agent-form" onSubmit={handleSubmit}>
                                {/* Profile Photo Section */}
                                <div className="mb-4 pb-4" style={{ borderBottom: "1px solid #e9ecef" }}>
                                    <h6 className="mb-3" style={{ fontWeight: "600", color: "#495057" }}>Profile Photo</h6>
                                    <div className="d-flex flex-column align-items-center">
                                        <div style={{ marginBottom: "16px" }}>
                                            <label htmlFor="profile-photo" className="btn btn-outline-primary" style={{ cursor: "pointer", borderRadius: "5px" }}>
                                                {profilePhotoUploading ? "Uploading..." : "Select Profile Photo"}
                                            </label>
                                            <input
                                                id="profile-photo"
                                                type="file"
                                                accept="image/jpeg,.jpg,.jpeg,image/gif,.gif,image/png,.png,image/webp,.webp"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        handleProfilePhotoSelect(file);
                                                    }
                                                    e.target.value = "";
                                                }}
                                                disabled={profilePhotoUploading || submitting}
                                                style={{ display: "none" }}
                                            />
                                        </div>
                                        {(profilePhotoPreview || formData.profile_photo_url) && (
                                            <div style={{ position: "relative", display: "inline-block" }}>
                                                <img
                                                    src={profilePhotoPreview || (formData.profile_photo_url?.startsWith("http") 
                                                        ? formData.profile_photo_url 
                                                        : formData.profile_photo_url 
                                                            ? (process.env.REACT_APP_API_BASE_URL + formData.profile_photo_url)
                                                            : "")}
                                                    alt="Profile Preview"
                                                    onError={(e) => {
                                                        e.target.style.display = "none";
                                                    }}
                                                    style={{
                                                        width: "120px",
                                                        height: "120px",
                                                        objectFit: "cover",
                                                        borderRadius: "5px",
                                                        border: "3px solid #e0e0e0",
                                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger"
                                                    onClick={handleProfilePhotoRemove}
                                                    disabled={profilePhotoUploading || submitting}
                                                    style={{
                                                        position: "absolute",
                                                        top: "-8px",
                                                        right: "-8px",
                                                        borderRadius: "50%",
                                                        width: "28px",
                                                        height: "28px",
                                                        padding: 0,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "18px",
                                                        lineHeight: "1"
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        )}
                                        <small className="text-muted mt-2" style={{ fontSize: "12px" }}>
                                            JPG, PNG, GIF, or WEBP - Max 5MB
                                        </small>
                                    </div>
                                </div>

                                {/* Basic Information Section */}
                                <div className="mb-4 pb-4" style={{ borderBottom: "1px solid #e9ecef" }}>
                                    <h6 className="mb-3" style={{ fontWeight: "600", color: "#495057" }}>Basic Information</h6>
                                    <div className="row">
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="agent_uid" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Agent Code</label>
                                            <input
                                                type="text"
                                                id="agent_uid"
                                                name="agent_uid"
                                                className="form-control"
                                                value={formData.agent_uid}
                                                onChange={handleChange}
                                                required
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="status" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Status</label>
                                            <select
                                                id="status"
                                                name="status"
                                                className="form-control"
                                                value={formData.status}
                                                onChange={handleChange}
                                                style={{ borderRadius: "5px" }}
                                            >
                                                {AgentStatuses.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label htmlFor="company_id" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Assign Company</label>
                                            <select
                                                id="company_id"
                                                name="company_id"
                                                className="form-control"
                                                value={formData.company_id}
                                                onChange={handleChange}
                                                required
                                                style={{ borderRadius: "5px" }}
                                            >
                                                <option value="">Select Company</option>
                                                {companies.map((company) => {
                                                    const label =
                                                        company.company_name ||
                                                        company.name ||
                                                        company.company_uid ||
                                                        `Company #${company.id}`;
                                                    return (
                                                        <option key={company.id} value={company.id}>
                                                            {label}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="name" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Agent Name <span style={{ color: "#dc3545" }}>*</span></label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                className="form-control"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Full name"
                                                required
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="designation" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Designation</label>
                                            <input
                                                type="text"
                                                id="designation"
                                                name="designation"
                                                className="form-control"
                                                value={formData.designation}
                                                onChange={handleChange}
                                                placeholder="e.g. Senior Insurance Advisor"
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Information Section */}
                                <div className="mb-4 pb-4" style={{ borderBottom: "1px solid #e9ecef" }}>
                                    <h6 className="mb-3" style={{ fontWeight: "600", color: "#495057" }}>Contact Information</h6>
                                    <div className="row">
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="email" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Email <span style={{ color: "#dc3545" }}>*</span></label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                className="form-control"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="phone" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Primary Phone</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                className="form-control"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="e.g. +91-98765-00001"
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="alternate_phone" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Alternate Phone</label>
                                            <input
                                                type="tel"
                                                id="alternate_phone"
                                                name="alternate_phone"
                                                className="form-control"
                                                value={formData.alternate_phone}
                                                onChange={handleChange}
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* License & Registration Section */}
                                <div className="mb-4 pb-4" style={{ borderBottom: "1px solid #e9ecef" }}>
                                    <h6 className="mb-3" style={{ fontWeight: "600", color: "#495057" }}>License & Registration</h6>
                                    <div className="row">
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="license_number" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>License Number <span style={{ color: "#dc3545" }}>*</span></label>
                                            <input
                                                type="text"
                                                id="license_number"
                                                name="license_number"
                                                className="form-control"
                                                value={formData.license_number}
                                                onChange={handleChange}
                                                required
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="registration_number" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Registration Number <span style={{ color: "#dc3545" }}>*</span></label>
                                            <input
                                                type="text"
                                                id="registration_number"
                                                name="registration_number"
                                                className="form-control"
                                                value={formData.registration_number}
                                                onChange={handleChange}
                                                required
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="id_number" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Government ID Number <span style={{ color: "#dc3545" }}>*</span></label>
                                            <input
                                                type="text"
                                                id="id_number"
                                                name="id_number"
                                                className="form-control"
                                                value={formData.id_number}
                                                onChange={handleChange}
                                                required
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="experience_years" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Experience (Years)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                id="experience_years"
                                                name="experience_years"
                                                className="form-control"
                                                value={formData.experience_years}
                                                onChange={handleChange}
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Address Section */}
                                <div className="mb-4 pb-4" style={{ borderBottom: "1px solid #e9ecef" }}>
                                    <h6 className="mb-3" style={{ fontWeight: "600", color: "#495057" }}>Address</h6>
                                    <small className="text-muted d-block mb-3" style={{ fontSize: "13px" }}>
                                        Office or primary working location of the agent.
                                    </small>
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <label htmlFor="address" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Street Address</label>
                                            <textarea
                                                id="address"
                                                name="address"
                                                className="form-control"
                                                rows="2"
                                                value={formData.address}
                                                onChange={handleChange}
                                                placeholder="House/Flat, Street, Landmark"
                                                style={{ borderRadius: "5px" }}
                                            ></textarea>
                                        </div>
                                        <div className="col-12 col-md-3 mb-3">
                                            <label htmlFor="city" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>City</label>
                                            <input
                                                type="text"
                                                id="city"
                                                name="city"
                                                className="form-control"
                                                value={formData.city}
                                                onChange={handleChange}
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-3 mb-3">
                                            <label htmlFor="state" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>State</label>
                                            <input
                                                type="text"
                                                id="state"
                                                name="state"
                                                className="form-control"
                                                value={formData.state}
                                                onChange={handleChange}
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-3 mb-3">
                                            <label htmlFor="country" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Country</label>
                                            <input
                                                type="text"
                                                id="country"
                                                name="country"
                                                className="form-control"
                                                value={formData.country}
                                                onChange={handleChange}
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-3 mb-3">
                                            <label htmlFor="postal_code" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Postal Code</label>
                                            <input
                                                type="text"
                                                id="postal_code"
                                                name="postal_code"
                                                className="form-control"
                                                value={formData.postal_code}
                                                onChange={handleChange}
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Information Section */}
                                <div className="mb-4 pb-4" style={{ borderBottom: "1px solid #e9ecef" }}>
                                    <h6 className="mb-3" style={{ fontWeight: "600", color: "#495057" }}>Additional Information</h6>
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <label htmlFor="notes" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Notes</label>
                                            <textarea
                                                id="notes"
                                                name="notes"
                                                className="form-control"
                                                rows="3"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                placeholder="Service territories, specializations, compliance notes etc."
                                                style={{ borderRadius: "5px" }}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>

                                {/* Credentials Section */}
                                <div className="mb-4">
                                    <h6 className="mb-3" style={{ fontWeight: "600", color: "#495057" }}>Credentials</h6>
                                    <small className="text-muted d-block mb-3" style={{ fontSize: "13px" }}>
                                        Leave password fields blank to keep existing password. Only fill if you want to change it.
                                    </small>
                                    <div className="row">
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="password" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Password</label>
                                            <input
                                                type="password"
                                                id="password"
                                                name="password"
                                                className="form-control"
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="Leave blank to keep existing password"
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="confirm_password" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Confirm Password</label>
                                            <input
                                                type="password"
                                                id="confirm_password"
                                                name="confirm_password"
                                                className="form-control"
                                                value={formData.confirm_password}
                                                onChange={handleChange}
                                                placeholder="Re-enter password if changing"
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6 mb-3">
                                            <label htmlFor="auth_key" style={{ fontWeight: "500", marginBottom: "6px", fontSize: "14px", color: "#495057" }}>Auth Key</label>
                                            <input
                                                type="text"
                                                id="auth_key"
                                                name="auth_key"
                                                className="form-control"
                                                value={formData.auth_key}
                                                onChange={handleChange}
                                                placeholder="Optional auth key"
                                                style={{ borderRadius: "5px" }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditAgent;

